import { type StudyRunCaseDashboardGateCardDto } from '@@types/api.types';
import { Tile } from '@components/shared/tile.component';
import { cn } from '@lib/utils';
import type { FC, ReactNode } from 'react';
import { formatDashboardScore, getGateStatusLabel, getGateStatusTone } from './meia-p4-run-cases.util';
import type { BriefStudyVerdict } from './meia-p5-brief.util';
import { getScoringBandLabel, getScoringBandTone } from './meia-scoring-band.util';

type MeiaP4KpiStripProps = {
  g4Gate?: StudyRunCaseDashboardGateCardDto | null;
  isLoading?: boolean;
  studyVerdict?: BriefStudyVerdict | null;
  tasksCount: number;
  technologiesCount: number;
  threadsCount: number;
};

type KpiCellProps = {
  isLoading?: boolean;
  label: string;
  value: ReactNode;
  valueClassName?: string;
};

const KpiCell: FC<KpiCellProps> = (props) => {
  const { isLoading, label, value, valueClassName } = props;
  return (
    <Tile
      className="p-3"
      contentClassName="gap-1"
    >
      <span className="text-sm font-medium text-gray-500">{label}</span>
      <span
        className={cn('font-mono text-sm font-semibold text-gray-900', isLoading && 'text-gray-300', valueClassName)}
      >
        {isLoading ? '—' : value}
      </span>
    </Tile>
  );
};

export const MeiaP4KpiStrip: FC<MeiaP4KpiStripProps> = (props) => {
  const { g4Gate, isLoading, studyVerdict, tasksCount, technologiesCount, threadsCount } = props;
  const hasVerdictScore = studyVerdict?.score !== null && studyVerdict?.score !== undefined;

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
      <KpiCell
        isLoading={isLoading}
        label="Tasks"
        value={tasksCount}
      />
      <KpiCell
        isLoading={isLoading}
        label="Technologies"
        value={technologiesCount}
      />
      <KpiCell
        isLoading={isLoading}
        label="Threads"
        value={threadsCount}
      />
      <KpiCell
        isLoading={isLoading}
        label="Study verdict"
        value={
          hasVerdictScore && studyVerdict
            ? `${formatDashboardScore(studyVerdict.score)} · ${getScoringBandLabel(studyVerdict.band)}`
            : '—'
        }
        valueClassName={getScoringBandTone(studyVerdict?.band).textClass}
      />
      <KpiCell
        isLoading={isLoading}
        label="G4 Gate"
        value={g4Gate ? getGateStatusLabel(g4Gate.status) : '—'}
        valueClassName={getGateStatusTone(g4Gate?.status).textClass}
      />
    </div>
  );
};
